import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import HelpButton from "../components/HelpButton";
import { endpoints } from "../apiConfig";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase, card, pageHeading } from "../theme";

// Curated, friendly-named list of common world time zones. The VALUE
// saved is still a real IANA identifier (what PHP's timezone engine
// actually needs to correctly handle daylight saving automatically) —
// only the LABEL shown to the user is the familiar name. A hardcoded
// "PST" can't do this correctly on its own since it doesn't know when
// to shift for daylight saving; the IANA identifier handles that for us.
const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKST/AKDT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PST/PDT)" },
  { value: "America/Denver", label: "Mountain Time (MST/MDT)" },
  { value: "America/Phoenix", label: "Arizona Time (MST, no DST)" },
  { value: "America/Chicago", label: "Central Time (CST/CDT)" },
  { value: "America/New_York", label: "Eastern Time (EST/EDT)" },
  { value: "America/Halifax", label: "Atlantic Time (AST/ADT)" },
  { value: "America/Sao_Paulo", label: "Brazil Time (Sao Paulo)" },
  { value: "Europe/London", label: "UK Time (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe Time (CET/CEST)" },
  { value: "Europe/Athens", label: "Eastern Europe Time (EET/EEST)" },
  { value: "Europe/Moscow", label: "Moscow Time (MSK)" },
  { value: "Africa/Cairo", label: "Egypt Time (Cairo)" },
  { value: "Africa/Johannesburg", label: "South Africa Time (SAST)" },
  { value: "Asia/Dubai", label: "Gulf Time (Dubai)" },
  { value: "Asia/Kolkata", label: "India Time (IST)" },
  { value: "Asia/Bangkok", label: "Indochina Time (Bangkok)" },
  { value: "Asia/Shanghai", label: "China Time (Shanghai)" },
  { value: "Asia/Tokyo", label: "Japan Time (JST)" },
  { value: "Asia/Seoul", label: "Korea Time (KST)" },
  { value: "Australia/Perth", label: "Australian Western Time (AWST)" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (AEST/AEDT)" },
  { value: "Pacific/Auckland", label: "New Zealand Time (NZST/NZDT)" },
];

function getTimezoneOptions() {
  return TIMEZONE_OPTIONS;
}

export default function SettingsPage() {
  const [businessName, setBusinessName] = useState("");

  // Profile fields
  const [profileBusinessName, setProfileBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // GPS location (used to check where employees clock in)
  const [geoLat, setGeoLat] = useState("");
  const [geoLng, setGeoLng] = useState("");
  const [geoRadius, setGeoRadius] = useState("100");
  const [locAccuracy, setLocAccuracy] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState("");
  const [locSaved, setLocSaved] = useState(false);
  const [emailRequestMessage, setEmailRequestMessage] = useState("");

  // Operational settings
  const [taxRate, setTaxRate] = useState(0);
  const [gratuityRate, setGratuityRate] = useState(0);
  const [gratuityMinPartySize, setGratuityMinPartySize] = useState(0);
  const [timezone, setTimezone] = useState("UTC");
  const [splitBillEnabled, setSplitBillEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  // Request a Limit Increase
  const [requestField, setRequestField] = useState("max_categories");
  const [requestValue, setRequestValue] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [myRequests, setMyRequests] = useState([]);

  const authHeader = () => getAuthHeader();

  const load = () => {
    if (!getAuthHeader().Authorization) {
      window.location.href = getLoginRedirect();
      return;
    }
    axios.get(endpoints.settings, { headers: authHeader() })
      .then((res) => {
        setTaxRate(res.data.tax_rate);
        setGratuityRate(res.data.gratuity_rate);
        setGratuityMinPartySize(res.data.gratuity_min_party_size);
        setTimezone(res.data.timezone || "UTC");
        setSplitBillEnabled(res.data.split_bill_enabled);
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          window.location.href = getLoginRedirect();
        }
      });

    axios.get(endpoints.profile, { headers: authHeader() })
      .then((res) => {
        setProfileBusinessName(res.data.business_name);
        setOwnerName(res.data.owner_name);
        setPhoneNumber(res.data.phone_number);
        setAddressLine1(res.data.address_line1 || "");
        setAddressLine2(res.data.address_line2 || "");
        setCity(res.data.city || "");
        setRegion(res.data.region || "");
        setPostalCode(res.data.postal_code || "");
        setGeoLat(res.data.geo_lat !== null && res.data.geo_lat !== undefined ? String(res.data.geo_lat) : "");
        setGeoLng(res.data.geo_lng !== null && res.data.geo_lng !== undefined ? String(res.data.geo_lng) : "");
        setGeoRadius(String(res.data.geo_radius_m ?? 100));
        setEmail(res.data.email);
        setPendingEmail(res.data.pending_email);
      })
      .catch(() => {});

    axios.get(endpoints.capRequest, { headers: authHeader() })
      .then((res) => setMyRequests(res.data))
      .catch(() => {});

    setBusinessName(getBusinessName());
  };

  useEffect(load, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileSaved(false);
    setProfileError("");
    let res;
    try {
      res = await axios.patch(
        endpoints.profile,
        {
          business_name: profileBusinessName, owner_name: ownerName, phone_number: phoneNumber,
          address_line1: addressLine1, address_line2: addressLine2, city, region, postal_code: postalCode,
        },
        { headers: authHeader() }
      );
    } catch (err) {
      setProfileError(err.response?.data?.error || "Could not save your profile.");
      return;
    }
    setProfileBusinessName(res.data.business_name);

    // Keep the Sidebar's cached business name in sync immediately,
    // rather than requiring a re-login to see the update reflected.
    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    if (tenant) {
      tenant.business_name = res.data.business_name;
      localStorage.setItem("tenant", JSON.stringify(tenant));
    }
    setBusinessName(res.data.business_name);

    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2500);
  };

  // Ask the browser for this device's position. Meant to be used while standing
  // in the restaurant; the fields stay editable in case the reading is off.
  const useMyLocation = () => {
    setLocError("");
    setLocSaved(false);
    if (!navigator.geolocation) {
      setLocError("This browser can't share its location. Enter the coordinates by hand instead.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLat(pos.coords.latitude.toFixed(6));
        setGeoLng(pos.coords.longitude.toFixed(6));
        setLocAccuracy(Math.round(pos.coords.accuracy));
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocError(
          err.code === 1
            ? "Location permission was denied. Allow location for this site in your browser settings, then try again."
            : "Couldn't get your location. Try again near a window or outdoors."
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    setLocError("");
    setLocSaved(false);
    try {
      const res = await axios.patch(
        endpoints.profile,
        { geo_lat: geoLat.trim(), geo_lng: geoLng.trim(), geo_radius_m: geoRadius },
        { headers: authHeader() }
      );
      setGeoLat(res.data.geo_lat !== null ? String(res.data.geo_lat) : "");
      setGeoLng(res.data.geo_lng !== null ? String(res.data.geo_lng) : "");
      setGeoRadius(String(res.data.geo_radius_m));
      setLocSaved(true);
      setTimeout(() => setLocSaved(false), 2500);
    } catch (err) {
      setLocError(err.response?.data?.error || "Could not save the location.");
    }
  };

  const clearLocation = () => {
    setGeoLat("");
    setGeoLng("");
    setLocAccuracy(null);
    setLocSaved(false);
  };

  const hasLocation = geoLat.trim() !== "" && geoLng.trim() !== "" && !Number.isNaN(Number(geoLat)) && !Number.isNaN(Number(geoLng));

  const requestEmailChange = async (e) => {
    e.preventDefault();
    setEmailRequestMessage("");
    try {
      const res = await axios.post(endpoints.profile, { new_email: newEmail }, { headers: authHeader() });
      setPendingEmail(res.data.pending_email);
      setNewEmail("");
      setEmailRequestMessage(res.data.message);
    } catch (err) {
      setEmailRequestMessage(err.response?.data?.error || "Could not request email change.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaved(false);
    await axios.patch(
      endpoints.settings,
      {
        tax_rate: parseFloat(taxRate) || 0,
        gratuity_rate: parseFloat(gratuityRate) || 0,
        gratuity_min_party_size: parseInt(gratuityMinPartySize, 10) || 0,
        timezone,
      },
      { headers: authHeader() }
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const submitCapRequest = async () => {
    setRequestMessage("");
    if (requestValue === "") {
      setRequestMessage("Enter a value first.");
      return;
    }
    try {
      const res = await axios.post(
        endpoints.capRequest,
        { field_name: requestField, requested_value: requestValue },
        { headers: authHeader() }
      );
      setRequestMessage(res.data.message);
      setRequestValue("");
      const refreshed = await axios.get(endpoints.capRequest, { headers: authHeader() });
      setMyRequests(refreshed.data);
    } catch (err) {
      setRequestMessage(err.response?.data?.error || "Could not submit request.");
    }
  };

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content settings-page" style={{ flex: 1, padding: space[6], maxWidth: 1600 }}>
        <h1 style={pageHeading}>Settings <HelpButton page="settings" /></h1>

        <div className="settings-cols">
          <section className="settings-section">
<h2 style={styles.sectionHeading}>Restaurant &amp; Owner Profile</h2>
        <form onSubmit={saveProfile} style={{ ...card, marginTop: space[3], display: "flex", flexDirection: "column", gap: space[3] }}>
          <div style={styles.row}>
            <div style={styles.cell(1, 200)}>
              <label style={styles.label}>Business Name</label>
              <input style={fullInput} value={profileBusinessName} onChange={(e) => setProfileBusinessName(e.target.value)} required />
            </div>
            <div style={styles.cell(1, 200)}>
              <label style={styles.label}>Owner Name</label>
              <input style={fullInput} value={ownerName} onChange={(e) => setOwnerName(e.target.value)} required />
            </div>
          </div>
          <div style={styles.row}>
            <div style={styles.cell(1, 170)}>
              <label style={styles.label}>Phone Number</label>
              <input style={fullInput} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
              <p style={styles.fieldHint}>Printed on your customer receipts.</p>
            </div>
            <div style={styles.cell(2, 240)}>
              <label style={styles.label}>Street Address</label>
              <input style={fullInput} value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} placeholder="e.g. 123 Main St" maxLength={255} />
            </div>
          </div>
          <div>
            <label style={styles.label}>Address Line 2 (optional)</label>
            <input style={fullInput} value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} placeholder="Suite, unit, floor" maxLength={255} />
          </div>
          <div style={styles.row}>
            <div style={styles.cell(2, 140)}>
              <label style={styles.label}>City</label>
              <input style={fullInput} value={city} onChange={(e) => setCity(e.target.value)} maxLength={120} />
            </div>
            <div style={styles.cell(1, 90)}>
              <label style={styles.label}>State / Region</label>
              <input style={fullInput} value={region} onChange={(e) => setRegion(e.target.value)} maxLength={100} />
            </div>
            <div style={styles.cell(1, 90)}>
              <label style={styles.label}>ZIP / Postal</label>
              <input style={fullInput} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={20} />
            </div>
          </div>
          <p style={{ ...styles.fieldHint, margin: 0 }}>
            Your name, address and phone are printed at the top of every receipt. Leave the street address blank to print just the name and phone.
          </p>
          {profileError && <div style={styles.errorText}>{profileError}</div>}
          <button style={buttonPrimary} type="submit">Save Profile</button>
          {profileSaved && <div style={styles.saved}>Profile saved.</div>}
        </form>
          </section>
          <section className="settings-section">
        <h2 style={styles.sectionHeading}>Restaurant Location (GPS)</h2>
        <form onSubmit={saveLocation} style={{ ...card, marginTop: space[3], display: "flex", flexDirection: "column", gap: space[3] }}>
          <p style={{ ...styles.fieldHint, marginTop: 0 }}>
            Used to check where employees are when they clock in (Employee module). Tap the button while you are
            standing in the restaurant, check the numbers, then save.
          </p>
          <div>
            <button type="button" style={buttonSecondary} onClick={useMyLocation} disabled={locating}>
              {locating ? "Finding your location…" : "Use my current location"}
            </button>
            {locAccuracy !== null && (
              <span style={{ ...styles.fieldHint, marginLeft: space[2] }} data-testid="loc-accuracy">
                Accurate to about {locAccuracy} m{locAccuracy > 100 ? ". Low accuracy, try again outdoors or near a window before saving" : ""}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 120px", minWidth: 0 }}>
              <label style={styles.label}>Latitude</label>
              <input style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} inputMode="decimal" value={geoLat} onChange={(e) => setGeoLat(e.target.value)} placeholder="e.g. 30.267153" data-testid="geo-lat" />
            </div>
            <div style={{ flex: "1 1 120px", minWidth: 0 }}>
              <label style={styles.label}>Longitude</label>
              <input style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} inputMode="decimal" value={geoLng} onChange={(e) => setGeoLng(e.target.value)} placeholder="e.g. -97.743057" data-testid="geo-lng" />
            </div>
            <div style={{ flex: "1 1 100px", minWidth: 0 }}>
              <label style={styles.label}>Radius (m)</label>
              <input style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} type="number" min="30" max="1000" value={geoRadius} onChange={(e) => setGeoRadius(e.target.value)} data-testid="geo-radius" />
            </div>
          </div>
          <p style={styles.fieldHint}>
            Radius: how far from this point counts as "at the restaurant" (30 to 1000 m, default 100 m).
            {hasLocation && (
              <>
                {" "}
                <a href={`https://www.google.com/maps?q=${encodeURIComponent(geoLat.trim())},${encodeURIComponent(geoLng.trim())}`} target="_blank" rel="noopener noreferrer" style={{ color: colors.brandBlue }}>
                  Check this spot in Maps
                </a>
              </>
            )}
          </p>
          {locError && <div style={styles.errorText} role="alert">{locError}</div>}
          <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
            <button style={buttonPrimary} type="submit">Save Location</button>
            <button type="button" style={buttonSecondary} onClick={clearLocation}>Clear</button>
          </div>
          {locSaved && <div style={styles.saved}>Location saved.</div>}
        </form>
          </section>
          <section className="settings-section">
            <h2 style={styles.sectionHeading}>Login Email</h2>
        <div style={{ ...card, marginTop: space[3] }}>
          <label style={styles.label}>Current Email</label>
          <div style={{ fontFamily: fonts.body, fontSize: 14, color: colors.textPrimary, marginBottom: space[3] }}>
            {email}
          </div>
          {pendingEmail ? (
            <div style={styles.infoBox}>
              Change to <strong>{pendingEmail}</strong> is pending approval from Alvna. Your
              login email won't change until it's approved.
            </div>
          ) : (
            <form className="inline-form" onSubmit={requestEmailChange} style={{ display: "flex", gap: space[2] }}>
              <input
                style={{ ...inputBase, flex: 1 }}
                type="email"
                placeholder="New email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
              <button style={buttonSecondary} type="submit">Change Email</button>
            </form>
          )}
          {emailRequestMessage && <p style={styles.fieldHint}>{emailRequestMessage}</p>}
        </div>
          </section>
          <section className="settings-section">
        <h2 style={styles.sectionHeading}>Operations</h2>
        <form onSubmit={save} style={{ ...card, marginTop: space[3], display: "flex", flexDirection: "column", gap: space[3] }}>
          <div>
            <label style={styles.label}>Time Zone</label>
            <select style={fullInput} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {!TIMEZONE_OPTIONS.some((tz) => tz.value === timezone) && (
                <option value={timezone}>{timezone} (previously set)</option>
              )}
              {getTimezoneOptions().map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <p style={styles.fieldHint}>
              Set this to wherever your restaurant actually is. Staff shift start/end times
              (set on the Staff page) are checked against this time zone.
            </p>
          </div>
          <div style={styles.row}>
            <div style={styles.cell(1, 120)}>
              <label style={styles.label}>Tax Rate (%)</label>
              <input style={fullInput} type="number" min="0" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
            </div>
            <div style={styles.cell(1, 120)}>
              <label style={styles.label}>Gratuity Rate (%)</label>
              <input style={fullInput} type="number" min="0" step="0.01" value={gratuityRate} onChange={(e) => setGratuityRate(e.target.value)} />
            </div>
            <div style={styles.cell(1, 150)}>
              <label style={styles.label}>Min. Party Size for Auto-Gratuity</label>
              <input
                style={fullInput}
                type="number"
                min="0"
                step="1"
                value={gratuityMinPartySize}
                onChange={(e) => setGratuityMinPartySize(e.target.value)}
                placeholder="0 = always apply"
              />
            </div>
          </div>
          <p style={{ ...styles.fieldHint, margin: 0 }}>
            Minimum party size: leave at 0 to always apply gratuity, regardless of party size. Set a number
            (e.g. 6) to only auto-apply gratuity for parties that size or larger. The
            waiter will need to enter Party Size before closing an order once this is set.
          </p>
          <div style={styles.infoBox}>
            Split Bill is currently <strong>{splitBillEnabled ? "enabled" : "disabled"}</strong> for
            your account. That setting is managed by Alvna, contact support to change it.
          </div>
          <button style={buttonPrimary} type="submit">Save Settings</button>
          {saved && <div style={styles.saved}>Settings saved.</div>}
        </form>
          </section>
        </div>

        <h2 style={styles.sectionHeading}>Request a Limit Increase</h2>
        <div style={{ ...card, marginTop: space[3] }}>
          <div className="settings-limits">
            <div>
              <p style={{ ...styles.fieldHint, marginTop: 0 }}>
                Need more room than your current plan? Submit a request, and Alvna is notified
                right away and will follow up once reviewed.
              </p>
              <div style={{ display: "flex", gap: space[2], marginTop: space[2], flexWrap: "wrap" }}>
                <select style={{ ...inputBase, flex: 1, minWidth: 160 }} value={requestField} onChange={(e) => setRequestField(e.target.value)}>
                  <option value="max_categories">Max Categories</option>
                  <option value="max_items_per_category">Max Items per Category</option>
                  <option value="max_tables">Max Tables</option>
                  <option value="max_staff">Max Staff Accounts</option>
                  <option value="report_retention_days">Report Retention (days)</option>
                  <option value="max_employees">Max Employees</option>
                  <option value="split_bill_enabled">Split Bill</option>
                  <option value="attendance_enabled">Employee Module</option>
                  <option value="online_ordering_enabled">Online Ordering</option>
                  <option value="online_pay_stripe_allowed">Online Card Payments (Stripe)</option>
                </select>
                {["split_bill_enabled", "attendance_enabled", "online_ordering_enabled", "online_pay_stripe_allowed"].includes(requestField) ? (
                  <select style={{ ...inputBase, width: 140 }} value={requestValue} onChange={(e) => setRequestValue(e.target.value)}>
                    <option value="1">Turn On</option>
                    <option value="0">Turn Off</option>
                  </select>
                ) : (
                  <input
                    style={{ ...inputBase, width: 100 }}
                    type="number"
                    min="0"
                    placeholder="New value"
                    value={requestValue}
                    onChange={(e) => setRequestValue(e.target.value)}
                  />
                )}
                <button style={buttonSecondary} onClick={submitCapRequest}>Submit Request</button>
              </div>
              {requestMessage && <p style={styles.fieldHint}>{requestMessage}</p>}
            </div>

            {myRequests.length > 0 && (
              <div>
                <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Your requests:</div>
                {myRequests.map((r) => (
                  <div key={r.id} style={styles.requestRow}>
                    <span>{FIELD_LABELS[r.field_name] || r.field_name}: {["split_bill_enabled", "attendance_enabled", "online_ordering_enabled", "online_pay_stripe_allowed"].includes(r.field_name) ? (r.requested_value === "1" ? "Turn On" : "Turn Off") : r.requested_value}</span>
                    <RequestStatusPill status={r.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const fullInput = { ...inputBase, width: "100%", boxSizing: "border-box" };

const FIELD_LABELS = {
  max_categories: "Max Categories",
  max_items_per_category: "Max Items per Category",
  max_tables: "Max Tables",
  max_staff: "Max Staff Accounts",
  report_retention_days: "Report Retention (days)",
  split_bill_enabled: "Split Bill",
  max_employees: "Max Employees",
  attendance_enabled: "Employee Module",
  online_ordering_enabled: "Online Ordering",
  online_pay_stripe_allowed: "Online Card Payments",
};

function RequestStatusPill({ status }) {
  const map = {
    pending: { bg: "#fef3c7", color: "#92400e" },
    approved: { bg: "#dcfce7", color: "#166534" },
    declined: { bg: "#fee2e2", color: "#991b1b" },
  };
  const s = map[status] || {};
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
}

const styles = {
  row: { display: "flex", gap: 16, flexWrap: "wrap" },
  cell: (grow, basis) => ({ flex: `${grow} 1 ${basis}px`, minWidth: 0 }),
  sectionHeading: { fontFamily: fonts.display, fontSize: 16, fontWeight: 600, color: colors.textPrimary, marginTop: space[6], marginBottom: 0 },
  label: { display: "block", fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, marginBottom: 4 },
  fieldHint: { fontFamily: fonts.body, fontSize: 11, color: colors.textMuted, marginTop: 4, lineHeight: 1.5 },
  infoBox: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, background: colors.surfaceMuted, padding: space[2], borderRadius: radius.sm },
  errorText: { fontFamily: fonts.body, fontSize: 13, color: "#b91c1c" },
  saved: { fontFamily: fonts.body, fontSize: 13, color: colors.statusReady, textAlign: "center" },
  requestRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
};
